import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  
  RefreshCw, 
  Search, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  TrendingUp,
  TrendingDown,
  Clock
} from 'lucide-react';
import { fetchFundingRates } from '../lib/coinglass';
import { FundingRateSimple } from '../types/coinglass';

interface FundingRatesTableProps {
  symbol?: string;
  showSearch?: boolean;
  showFilters?: boolean;
  pageSize?: number;
}

type SortField = 'exchangeName' | 'fundingRate' | 'nextFundingTime' | 'predicatedRate';
type SortOrder = 'asc' | 'desc';

const FundingRatesTable: React.FC<FundingRatesTableProps> = ({
  symbol = 'BTC',
  showSearch = true,
  showFilters = true,
  pageSize = 20
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('fundingRate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [filterType, setFilterType] = useState<'all' | 'positive' | 'negative'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, error, isLoading, refetch } = useQuery<FundingRateSimple[]>({
    queryKey: ['fundingRates', symbol],
    queryFn: () => fetchFundingRates(symbol),
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    const filtered = data.filter((item) => {
      const matchesSearch = item.exchangeName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = 
        filterType === 'all' ||
        (filterType === 'positive' && item.fundingRate > 0) ||
        (filterType === 'negative' && item.fundingRate < 0);
      
      return matchesSearch && matchesFilter;
    });    // Sort the data
    filtered.sort((a, b) => {
      let aValue: string | number = a[sortField as keyof typeof a];
      let bValue: string | number = b[sortField as keyof typeof b];

      if (sortField === 'fundingRate' || sortField === 'predicatedRate') {
        aValue = Math.abs(aValue as number);
        bValue = Math.abs(bValue as number);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, searchTerm, sortField, sortOrder, filterType]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const formatRate = (rate: number) => {
    const percentage = (rate * 100).toFixed(4);
    return `${rate >= 0 ? '+' : ''}${percentage}%`;
  };

  const formatNextFunding = (timestamp: number) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffMs <= 0) return 'Now';
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}m`;
    return `${diffMinutes}m`;
  };

  const getRateColor = (rate: number) => {
    if (rate > 0.001) return 'text-green-400';
    if (rate > 0) return 'text-green-300';
    if (rate < -0.001) return 'text-red-400';
    if (rate < 0) return 'text-red-300';
    return 'text-gray-400';
  };

  const getChangeIcon = (current: number, predicted: number) => {
    if (predicted > current) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (predicted < current) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return null;
  };

  if (isLoading) return null;
  if (error) return null; // Silently hide when data unavailable
  if (!data || data.length === 0) return null; // Silently hide empty state

  return (
    <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-white">{symbol} Funding Rates</h3>
            <p className="text-sm text-gray-400 mt-1">
              Real-time funding rates across {data?.length || 0} exchanges
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Search and Filters */}
        {(showSearch || showFilters) && (
          <div className="flex flex-col sm:flex-row gap-4">
            {showSearch && (
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search exchanges..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                />
              </div>
            )}

            {showFilters && (
              <div className="flex gap-2">
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value as typeof filterType);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                >
                  <option value="all">All Rates</option>
                  <option value="positive">Positive Only</option>
                  <option value="negative">Negative Only</option>
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('exchangeName')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                >
                  Exchange
                  {getSortIcon('exchangeName')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('fundingRate')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                >
                  Funding Rate
                  {getSortIcon('fundingRate')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('predicatedRate')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                >
                  Predicted Rate
                  {getSortIcon('predicatedRate')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('nextFundingTime')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                >
                  Next Funding
                  {getSortIcon('nextFundingTime')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Trend
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {paginatedData.map((item) => (
              <tr key={item.exchangeName} className="hover:bg-zinc-800/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-white">
                    {item.exchangeName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-mono font-semibold ${getRateColor(item.fundingRate)}`}>
                    {formatRate(item.fundingRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-mono ${getRateColor(item.predicatedRate)}`}>
                    {formatRate(item.predicatedRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Clock className="w-4 h-4" />
                    {formatNextFunding(item.nextFundingTime)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getChangeIcon(item.fundingRate, item.predicatedRate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 border-t border-zinc-800 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm bg-zinc-800 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-700 transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm bg-zinc-800 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-700 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundingRatesTable;
